import { createContext, ReactNode, useContext, useMemo, useRef, useState } from 'react';
import { Callback, LibraryContextType, ResourceReference, TypeFilter, TypeFilterFunction } from './types';
import { useAppDispatch, useAppSelector } from '../../store';
import { useResourceSearch } from '../resources';
import type { LibraryResource, Resource, ResourceSearchResult } from '../resources/types';
import { uiActions } from '../ui/slice';
import { playlistActions } from '../playlist/slice';

const DEFAULT_CONTEXT: LibraryContextType = {
  focusedIndex: -1,
  setFocusedIndex: () => {},
  searchQuery: '',
  setSearchQuery: () => {},
  typeFilter: TypeFilter.ALL,
  setTypeFilter: () => {},
  resources: {},
  filteredResources: [],
  searchboxRef: { current: null },

  addResourceToPlaylist: () => {},
  previewResource: () => {},
  sendResourceToLive: () => {},
};

const LibraryContext = createContext<LibraryContextType>(DEFAULT_CONTEXT);

// eslint-disable-next-line react-refresh/only-export-components
export const useLibraryContext = () => useContext(LibraryContext);

type Props = {
  children: ReactNode | ReactNode[]
}

const sortLibrary = (a: LibraryResource, b: LibraryResource) => {
  if (b.meta.lastUsed) {
    if (a.meta.lastUsed) {
      return b.meta.lastUsed - a.meta.lastUsed;
    }
    // b has been used, a has not
    return 1;
  }
  else if (a.meta.lastUsed) {
    // a has been used, b has not
    return -1;
  }
  else {
    return a.title.localeCompare(b.title);
  }
};

const isNumberRef = (ref: ResourceReference): ref is number => {
  return Number.isInteger(ref);
};

const sortSearchResults = (a: ResourceSearchResult, b: ResourceSearchResult) => sortLibrary(a.resource, b.resource);

export const LibraryContextProvider = ({ children }: Props) => {

  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<TypeFilterFunction>(() => TypeFilter.ALL);
  const searchboxRef = useRef<HTMLInputElement | null>(null);

  const resources = useAppSelector(state => state.resources);

  const searchResults = useResourceSearch(searchQuery);

  const filteredResources = useMemo(
    () => {
      if (searchResults.returned || searchResults.searching) {
        const adaptedFilter = (rsr: ResourceSearchResult) => typeFilter(rsr.resource);
        return searchResults.results.filter(adaptedFilter).sort(sortSearchResults);
      }
      else {
        return Object.values(resources).filter(r => !!r).filter(typeFilter).sort(sortLibrary).map(
          r => ({
            resource: r,
            locations: []
          }));
      }
    },
    [resources, searchResults, typeFilter]
  );

  const dispatch = useAppDispatch();

  const resourceFromReference = (ref: ResourceReference): Resource => {
    let resource: Resource;

    if (isNumberRef(ref)) {
      resource = filteredResources[ref as number].resource;
    }
    else {
      resource = ref as Resource;
    }
    return resource;
  };

  const addResourceToPlaylist = (ref: ResourceReference, callback?: Callback) => {
    dispatch(playlistActions.addItem({ resource: resourceFromReference(ref) }));
    if (callback) {
      callback();
    }
  };

  const previewResource = (ref: ResourceReference, callback?: Callback) => {
    dispatch(uiActions.setCurrentPreviewResource({ resource: resourceFromReference(ref) }));
    if (callback) {
      callback();
    }
  };

  const sendResourceToLive = (ref: ResourceReference, callback?: Callback) => {
    dispatch(uiActions.setCurrentLiveResource({ resource: resourceFromReference(ref) }));
    searchboxRef.current?.blur();
    if (callback) {
      callback();
    }
  };

  return (
    <LibraryContext.Provider
      value={{
        focusedIndex,
        setFocusedIndex,
        searchQuery,
        setSearchQuery,
        typeFilter,
        setTypeFilter,
        searchboxRef,
        resources,
        filteredResources,
        previewResource,
        addResourceToPlaylist,
        sendResourceToLive
      }}
    >
      { children }
    </LibraryContext.Provider>
  );
};
